import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import * as fromClientsControlActions from '../actions/clients-control.actions';
import { Observable, of, combineLatest } from 'rxjs';
import { map, catchError, switchMap, take } from 'rxjs/operators';
import { FirebaseService } from '../../services/firebase/firebase.service';
import {
  AngularFirestore,
  DocumentReference
} from '@angular/fire/compat/firestore';
import { WeightFirebaseResponse } from 'src/app/models/weights.model';
import { PerimeterFirebaseResponse } from 'src/app/models/perimeters.model';
import { FoldFirebaseResponse } from 'src/app/models/folds.model';
import { User } from 'src/app/models/user.model';

@Injectable()
export class ClientsControlEffects {
  constructor(
    private readonly action$: Actions,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore
  ) {}

  // Fetch Clients Control
  fetchClientsControl$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromClientsControlActions.FetchClientsControlAction),
      switchMap(({ id }) => {
        const userRef = this.firestore.doc(`users/${id}`).ref;
        return combineLatest([
          userRef.get(),
          this.getWeights(userRef),
          this.getPerimeters(userRef),
          this.getFolds(userRef)
        ]);
      }),
      switchMap((data) => {
        const clientData: User = {
          ...(data[0].data() as User),
          id: data[0].id
        };
        const weightControl = data[1].length > 0 ? data[1][0] : { weights: [] };
        const perimeterControl =
          data[1].length > 0 ? data[2][0] : { registers: [] };
        const foldsControl =
          data[2].length > 0 ? data[3][0] : { registers: [] };

        delete weightControl.userRef;
        delete perimeterControl.userRef;
        delete foldsControl.userRef;
        delete clientData.teamRef;
        delete clientData.trainerRef;

        return of({
          clientData,
          weightControl,
          perimeterControl,
          foldsControl
        });
      }),
      map((data) => ({
        type: fromClientsControlActions.FETCH_CLIENTS_CONTROL_SUCCESS,
        data
      })),
      catchError((error) =>
        of({
          type: fromClientsControlActions.FETCH_CLIENTS_CONTROL_FAIL,
          error: error
        })
      )
    )
  );

  private getWeights(
    userRef: DocumentReference<unknown>
  ): Observable<WeightFirebaseResponse[]> {
    return this.firebaseService
      .getDataOfCollectionByAnyField('weights', 'userRef', '==', userRef)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => {
            const weightFirebaseResponse: WeightFirebaseResponse =
              doc.data() as WeightFirebaseResponse;
            weightFirebaseResponse.weights = weightFirebaseResponse.weights.map(
              (weight) => ({
                ...weight,
                date: new Date(
                  (weight.date as unknown as { seconds: number }).seconds * 1000
                )
              })
            );
            return {
              ...weightFirebaseResponse,
              id: doc.id
            };
          })
        )
      );
  }

  private getPerimeters(
    userRef: DocumentReference<unknown>
  ): Observable<PerimeterFirebaseResponse[]> {
    return this.firebaseService
      .getDataOfCollectionByAnyField('perimeters', 'userRef', '==', userRef)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => {
            const perimeterFirebaseResponse: PerimeterFirebaseResponse =
              doc.data() as PerimeterFirebaseResponse;
            perimeterFirebaseResponse.registers =
              perimeterFirebaseResponse.registers.map((register) => ({
                ...register,
                date: new Date(
                  (register.date as unknown as { seconds: number }).seconds *
                    1000
                )
              }));
            return {
              ...perimeterFirebaseResponse,
              id: doc.id
            };
          })
        )
      );
  }

  private getFolds(
    userRef: DocumentReference<unknown>
  ): Observable<FoldFirebaseResponse[]> {
    return this.firebaseService
      .getDataOfCollectionByAnyField('folds', 'userRef', '==', userRef)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => {
            const foldFirebaseResponse: FoldFirebaseResponse =
              doc.data() as FoldFirebaseResponse;
            foldFirebaseResponse.registers = foldFirebaseResponse.registers.map(
              (register) => ({
                ...register,
                date: new Date(
                  (register.date as unknown as { seconds: number }).seconds *
                    1000
                )
              })
            );
            return {
              ...foldFirebaseResponse,
              id: doc.id
            };
          })
        )
      );
  }
}
